import { useCallback, useState } from 'react';
import { Skeleton, Stack, Text } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import type { EnvironmentalCalculations, EnvironmentalDataFormData } from '../../../types/reports.js';
import { FormSubsection } from '../../ui/FormSubsection.js';
import { SectionToggleRow } from '../../ui/SectionToggleRow.js';
import { KilnLoadCalculationForm } from './KilnLoadCalculationForm.js';
import { countFilledLoadComponents, KILN_LOAD_COMPONENT_KEYS } from './kilnFields.js';
import { hasWeightedFlow } from './kilnLoadFormat.js';
import { kilnTitle } from './kilnTitle.js';

interface KilnLoadSectionProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  loadCalc: EnvironmentalCalculations | null;
  disabled?: boolean;
  loading?: boolean;
  reportingPeriod?: string;
}

interface KilnLoadMetaProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  kilnIdx: number;
  hasResult: boolean;
}

/**
 * Kopfzaehler der Ofenkarte (Plan 38.1-02, erweitert 40-06, Sketch 024 `meta()`) -- isolierte
 * Blattkomponente, Muster `KilnCountBadge` (EnvironmentalDataForm.tsx): abonniert per `form.watch`
 * direkt im Render-Body (kein umschliessendes `useEffect`, sonst "Invalid hook call", siehe
 * `MeasurementTitle` in KilnMeasurementCard.tsx). Beobachtet das GESAMTE `loadCalculation`-Objekt
 * (nicht nur die Konzentrationsfelder) -- `form.watch` meldet damit auch eine Aenderung von
 * `calculationBasis` allein (etwa durch das SegmentedControl in 40-07), ohne dass sich sonst
 * irgendein Feld aendert; `countFilledLoadComponents` aus `kilnFields.ts` ist selbst
 * modusabhaengig (D-09) und braucht dafuer keine eigene Logik hier. "keine Angaben" nur, wenn WEDER
 * eine Angabe im Formularstand erfasst ist NOCH der Server ein Ergebnis liefert (`hasResult` aus
 * `hasWeightedFlow`, keine eigene Rechnung).
 */
function KilnLoadMeta({ form, kilnIdx, hasResult }: KilnLoadMetaProps) {
  const [count, setCount] = useState<number>(() =>
    countFilledLoadComponents(
      form.getValues().rotaryKilns?.[kilnIdx]?.loadCalculation as
        | Record<string, unknown>
        | null
        | undefined,
    ),
  );

  const handleChange = useCallback(({ value }: { value: unknown }) => {
    setCount(countFilledLoadComponents(value as Record<string, unknown> | null | undefined));
  }, []);

  form.watch(`rotaryKilns.${kilnIdx}.loadCalculation`, handleChange);

  if (count === 0 && !hasResult) {
    return <>keine Angaben</>;
  }
  return (
    <>
      {count} von {KILN_LOAD_COMPONENT_KEYS.length} Komponenten
    </>
  );
}

/**
 * Reiter "Frachtenberechnung" (D-18/D-21, Sketch 022 Variante D, Plan 38.1-02): Reiterueberschrift
 * mit Erklaertext als `FormSubsection` (SC-1), jeder Ofen als `SectionToggleRow` im card-Modus
 * -- exakt wie im Reiter Drehoefen (DrehofenSection.tsx) -- mit dem Ofennamen samt
 * Stilllegungs-Suffix als Titel und dem Kopfzaehler `KilnLoadMeta` als `meta` (SC-2). Iteriert
 * dieselbe Ofenliste wie `DrehofenSection` und rendert je Ofen ein Rechenblatt -- OHNE
 * Sonderbehandlung (D-17): auch stillgelegte Oefen und solche ohne eigenes Anlegerecht
 * (`allowsOwnMeasurements = false`) bekommen ihr Blatt. Die dortige Sperre schuetzt die
 * Messungen vor eigenen Eintraegen; hier gibt der Melder ohnehin eigene, nicht-validierte Zahlen
 * ein (D-12), und das Ergebnis geht nirgendwohin. `disabled` kommt ausschliesslich aus dem
 * Meldungsstatus-Prop, nie aus dem Ofen selbst.
 */
export function KilnLoadSection({
  form,
  loadCalc,
  disabled,
  loading,
  reportingPeriod,
}: KilnLoadSectionProps) {
  const kilns = form.getValues().rotaryKilns ?? [];
  // reportingPeriod ist ein ISO-Datumsstring ("2026-01-01") -- die ersten vier Zeichen sind das
  // Jahr, dasselbe Muster wie DrehofenSection.tsx.
  const reportingYear = reportingPeriod?.slice(0, 4);

  // D-11: dieselbe Vorbelegung wie die Ofen-Ebene im Reiter Drehoefen (initial alle offen).
  const [expandedKilns, setExpandedKilns] = useState<Set<number>>(
    () => new Set(kilns.map((_, i) => i)),
  );

  function toggleKiln(idx: number): void {
    setExpandedKilns((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  if (loading) {
    return (
      <Stack gap="md">
        <Skeleton height={240} />
        <Skeleton height={240} />
      </Stack>
    );
  }

  return (
    <FormSubsection
      title="Frachtenberechnung"
      description={
        <>
          <Text component="span" inherit display="block">
            Hier rechnen Sie die Jahresfrachten Ihrer Drehöfen nach, etwa um sie mit Ihrer eigenen
            Berechnung abzugleichen.
          </Text>
          <Text component="span" inherit display="block">
            Frachten werden aus den{' '}
            <Text component="span" inherit fw={700}>
              nicht validierten
            </Text>{' '}
            Messwerten berechnet. Alle Angaben sind freiwillig und haben keinen Einfluss auf das
            Einreichen.
          </Text>
        </>
      }
    >
      {kilns.length === 0 ? (
        // Wortlaut identisch zu DrehofenSection.tsx (D-24, UI-SPEC Punkt 11): Werke ohne Ofen
        // behalten den Reiter, kein dynamisches Ausblenden -- nur ein ruhiger Hinweis.
        <Text size="sm" c="vdzGray.6">
          Für dieses Werk sind keine Drehöfen hinterlegt. Sollte das nicht stimmen, bitte beim VDZ
          melden.
        </Text>
      ) : (
        <Stack gap="md">
          {kilns.map((kiln, idx) => (
            <SectionToggleRow
              key={`k-${idx}`}
              variant="card"
              title={kilnTitle(kiln, idx, reportingYear)}
              meta={
                <KilnLoadMeta
                  form={form}
                  kilnIdx={idx}
                  hasResult={hasWeightedFlow(loadCalc?.rotaryKilnLoads?.[idx])}
                />
              }
              expanded={expandedKilns.has(idx)}
              onToggle={() => toggleKiln(idx)}
            >
              <KilnLoadCalculationForm
                form={form}
                kilnIdx={idx}
                result={loadCalc?.rotaryKilnLoads?.[idx] ?? null}
                disabled={disabled}
              />
            </SectionToggleRow>
          ))}
        </Stack>
      )}
    </FormSubsection>
  );
}
